import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
// Iconos del módulo Soporte (equivalentes MUI de los lucide sugeridos, para
// coherencia visual con el resto del popover que ya usa @mui/icons-material).
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
    fetchMisNotificaciones,
    fetchContadorNoLeidas,
    marcarNotificacionLeida,
    marcarTodasLeidas
} from '../../../peticiones_API/PeticionesNotificaciones';
import {
    getNotifInventario,
    getContadorInventario,
    marcarLeidaInventario,
    marcarTodasInventario
} from '../../../peticiones_API/PeticionesNotificacionesInventario';
import {
    getNotifIntervenciones,
    getContadorIntervenciones,
    marcarLeidaIntervenciones,
    marcarTodasIntervenciones
} from '../../../peticiones_API/PeticionesNotificacionesIntervenciones';
import './NotificacionesPopover.css';

function NotificacionesPopover() {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificaciones, setNotificaciones] = useState([]);
    const [contador, setContador] = useState(0);
    const [loading, setLoading] = useState(false);
    const intervalRef = useRef(null);
    const contadorAnterior = useRef(null);
    const esPrimeraCarga = useRef(true);

    const solicitarPermisoNotificaciones = useCallback(async () => {
        if (!('Notification' in window)) {
            console.log('Este navegador no soporta notificaciones');
            return;
        }

        if (Notification.permission === 'default') {
            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    console.log('Permisos de notificación concedidos');
                }
            } catch (error) {
                console.error('Error al solicitar permisos:', error);
            }
        }
    }, []);

    const mostrarNotificacionNavegador = useCallback((titulo, mensaje) => {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return;
        }

        try {
            const notif = new Notification(titulo, {
                body: mensaje,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: 'intranet-notification',
                requireInteraction: false
            });

            notif.onclick = () => {
                window.focus();
                notif.close();
            };

            setTimeout(() => notif.close(), 5000);
        } catch (error) {
            console.error('Error al mostrar notificación:', error);
        }
    }, []);

    const cargarContador = useCallback(async () => {
        // Sumamos ambos contadores; si una fuente falla, el otro origen sigue.
        const [vac, inv, intv] = await Promise.allSettled([
            fetchContadorNoLeidas(),
            getContadorInventario(),
            getContadorIntervenciones(),
        ]);
        const nVac = vac.status === 'fulfilled' ? Number(vac.value?.no_leidas || 0) : 0;
        const nInv = inv.status === 'fulfilled' ? Number(inv.value?.no_leidas || 0) : 0;
        const nIntv = intv.status === 'fulfilled' ? Number(intv.value?.no_leidas || 0) : 0;
        const nuevoContador = nVac + nInv + nIntv;

        if (!esPrimeraCarga.current && nuevoContador > contadorAnterior.current && contadorAnterior.current !== null) {
            const diferencia = nuevoContador - contadorAnterior.current;
            mostrarNotificacionNavegador(
                diferencia === 1 ? 'Nueva notificación' : `${diferencia} nuevas notificaciones`,
                'Tienes nuevas notificaciones'
            );
        }

        esPrimeraCarga.current = false;
        contadorAnterior.current = nuevoContador;
        setContador(nuevoContador);
    }, [mostrarNotificacionNavegador]);

    const cargarNotificaciones = useCallback(async () => {
        try {
            setLoading(true);
            const [vac, inv, intv] = await Promise.allSettled([
                fetchMisNotificaciones(),
                getNotifInventario(),
                getNotifIntervenciones(),
            ]);
            const items = [];
            if (vac.status === 'fulfilled' && Array.isArray(vac.value)) {
                for (const n of vac.value) items.push({ ...n, origen: 'vacaciones' });
            }
            if (inv.status === 'fulfilled' && Array.isArray(inv.value)) {
                for (const n of inv.value) items.push({ ...n, origen: 'inventario' });
            }
            if (intv.status === 'fulfilled' && Array.isArray(intv.value)) {
                for (const n of intv.value) items.push({ ...n, origen: 'intervenciones' });
            }
            // Combinado ordenado por createdAt DESC.
            items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setNotificaciones(items);
        } catch (error) {
            console.error('Error al cargar notificaciones:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        solicitarPermisoNotificaciones();
        cargarContador();
        intervalRef.current = setInterval(cargarContador, 600000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [cargarContador, solicitarPermisoNotificaciones]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        cargarNotificaciones();
        cargarContador();  // badge cuadra al instante con la lista
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMarcarLeida = async (notif) => {
        try {
            if (notif.origen === 'inventario') {
                await marcarLeidaInventario(notif.Id);
            } else if (notif.origen === 'intervenciones') {
                await marcarLeidaIntervenciones(notif.Id);
            } else {
                await marcarNotificacionLeida(notif.Id);
            }
            setNotificaciones(prev =>
                prev.map(n => (n.origen === notif.origen && n.Id === notif.Id) ? { ...n, leida: true } : n)
            );
            setContador(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error al marcar como leida:', error);
        }
    };

    const handleNotificacionClick = async (notif) => {
        if (!notif.leida) {
            await handleMarcarLeida(notif);
        }
        handleClose();
        if (notif.origen === 'inventario' || notif.origen === 'intervenciones') {
            // Se navega con el `link` tal cual lo guardan los motores
            // (p. ej. "/inventario/coche/123" o "/citas-tecnicas/mis-campanas").
            // Si no hay link, solo cierra.
            if (notif.link) navigate(notif.link);
            return;
        }
        // Vacaciones (comportamiento previo).
        const solicitante = notif.SolicitudVacacion?.solicitante;
        if (solicitante && notif.tipo === 'solicitud') {
            navigate(`/panel/vacaciones/aprobaciones?persona_id=${solicitante.Id}&persona_nombre=${encodeURIComponent(solicitante.nombre + ' ' + solicitante.apellidos)}`);
        } else {
            navigate('/panel/vacaciones/aprobaciones');
        }
    };

    const handleMarcarTodasLeidas = async () => {
        const res = await Promise.allSettled([
            marcarTodasLeidas(),
            marcarTodasInventario(),
            marcarTodasIntervenciones(),
        ]);
        const algunFallo = res.some((r) => r.status === 'rejected');
        if (algunFallo) console.error('Algún origen falló al marcar todas como leídas:', res);
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
        setContador(0);
    };

    const getIconByItem = (notif) => {
        // Soporte: el tipo SOP_* es inequívoco (llega por la fuente in-app
        // genérica). Color corporativo navy del módulo (#003c66).
        if (notif.tipo && notif.tipo.startsWith('SOP_')) {
            const navy = { color: '#003c66' };
            switch (notif.tipo) {
                case 'SOP_TICKET_CREADO_USUARIO': return <MoveToInboxIcon sx={navy} />;
                case 'SOP_ESTADO_EN_DESARROLLO':  return <BuildIcon sx={navy} />;
                case 'SOP_ESTADO_ACEPTADA':       return <ThumbUpIcon sx={navy} />;
                case 'SOP_RESUELTO':              return <CheckCircleIcon sx={navy} />;
                case 'SOP_IMPLEMENTADA':          return <AutoAwesomeIcon sx={navy} />;
                case 'SOP_ASIGNADO_TECNICO':      return <HowToRegIcon sx={navy} />;
                case 'SOP_COMENTARIO_USUARIO':
                case 'SOP_COMENTARIO_TECNICO':    return <ChatBubbleOutlineIcon sx={navy} />;
                default:                          return <SupportAgentIcon sx={navy} />;
            }
        }
        if (notif.origen === 'inventario') {
            if (notif.tipo === 'evento') {
                switch (notif.event_type) {
                    case 'asignacion':       return <AssignmentIndIcon sx={{ color: '#2196f3' }} />;
                    case 'devolucion':       return <AssignmentReturnIcon sx={{ color: '#2196f3' }} />;
                    case 'baja':             return <RemoveCircleOutlineIcon sx={{ color: '#2196f3' }} />;
                    case 'reparacion_inicio':
                    case 'reparacion_fin':   return <BuildIcon sx={{ color: '#2196f3' }} />;
                    default:                 return <SwapHorizIcon sx={{ color: '#2196f3' }} />;
                }
            }
            if (notif.tipo === 'vencimiento') return <CalendarMonthIcon sx={{ color: '#2196f3' }} />;
            if (notif.tipo === 'stock_bajo')  return <Inventory2Icon sx={{ color: '#2196f3' }} />;
            return <NotificationsIcon sx={{ color: '#2196f3' }} />;
        }
        if (notif.origen === 'intervenciones') {
            // Color corporativo del módulo (#003c66)
            if (notif.tipo === 'CITA_RESERVADA_USUARIO') return <CheckCircleIcon sx={{ color: '#003c66' }} />;
            if (notif.tipo === 'CITA_NUEVA_TECNICO')     return <EventAvailableIcon sx={{ color: '#003c66' }} />;
            if (notif.tipo && notif.tipo.startsWith('CITA_CANCELADA')) return <CancelIcon sx={{ color: '#003c66' }} />;
            return <CalendarMonthIcon sx={{ color: '#003c66' }} />;
        }
        // Vacaciones
        switch (notif.tipo) {
            case 'solicitud':
                return <AccessTimeIcon sx={{ color: '#ff9800' }} />;
            case 'aprobacion':
                return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
            case 'rechazo':
                return <CancelIcon sx={{ color: '#f44336' }} />;
            default:
                return <EventAvailableIcon sx={{ color: '#2196f3' }} />;
        }
    };

    const formatearFecha = (fecha) => {
        const date = new Date(fecha);
        const ahora = new Date();
        const diffMs = ahora - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHoras = Math.floor(diffMs / 3600000);
        const diffDias = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Ahora mismo';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHoras < 24) return `Hace ${diffHoras}h`;
        if (diffDias < 7) return `Hace ${diffDias} días`;
        return date.toLocaleDateString('es-ES');
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{ color: '#336385', mr: 1 }}
            >
                <Badge
                    badgeContent={contador}
                    color="error"
                    max={99}
                >
                    {contador > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { width: 360, maxHeight: 480 }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#336385' }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        Notificaciones
                    </Typography>
                    {contador > 0 && (
                        <Button
                            size="small"
                            startIcon={<DoneAllIcon />}
                            onClick={handleMarcarTodasLeidas}
                            sx={{ color: 'white', textTransform: 'none' }}
                        >
                            Marcar todas
                        </Button>
                    )}
                </Box>
                <Divider />

                {loading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">Cargando...</Typography>
                    </Box>
                ) : notificaciones.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <NotificationsNoneIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                        <Typography color="text.secondary">No tienes notificaciones</Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
                        {notificaciones.map((notif) => (
                            <ListItem
                                key={`${notif.origen}-${notif.Id}`}
                                button
                                onClick={() => handleNotificacionClick(notif)}
                                sx={{
                                    backgroundColor: notif.leida ? 'transparent' : 'rgba(33, 150, 243, 0.08)',
                                    borderLeft: notif.leida ? 'none' : '3px solid #2196f3',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                    cursor: 'pointer'
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    {getIconByItem(notif)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: notif.leida ? 'normal' : 'bold' }}
                                        >
                                            {notif.titulo}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography variant="caption" component="span" sx={{ display: 'block' }}
                                                dangerouslySetInnerHTML={{ __html: notif.mensaje }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatearFecha(notif.createdAt)}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    );
}

export default NotificacionesPopover;
